import {GridColDef, GridValueGetter} from '@mui/x-data-grid-pro';
import moment from 'moment';
import {Goal} from '../../../schema-types/Goal';
import {getCurrencyString} from '../../../utils/currencyFormatter';
import {themeColors} from '../../../theme/Theme';
import {RenderColor} from '../../../components/EditorGrid/GridRenderComponents';
import {dateFormatter} from '../Goals/utils';
import {getGoalName} from './utils';
import {StatusAtReviewEnum} from '../../../schema-types/globalTypes';

// ATTENTION: Goal columns may be undefined except for name and progress
const UNDEFINED = 'Undefined';

export const renderCellGoalProgress = RenderColor({
  Completed: themeColors.lightGreen,
  Submitted: themeColors.lightBlue,
  Partial: themeColors.lightYellow,
  Error: themeColors.lightAlert,
  Default: themeColors.greyLight,
});

export const valueGetterGoalProgress: GridValueGetter<Goal> = (params, row) => {
  if (row?.isFailedSubmission) {
    return 'Error';
  }
  if (row?.curoId) {
    return 'Submitted';
  }
  return 'Partial';
};

export const valueGetterGoalName: GridValueGetter<Goal> = (params, row) =>
  getGoalName(row as Goal);

export const valueGetterGoalPriority: GridValueGetter<Goal> = (params, row) =>
  row?.priority ? row.priority : UNDEFINED;

export const renderCellGoalPriority = RenderColor({
  '5 - Very low': themeColors.blue,
  '4 - Low': themeColors.lightBlue,
  '3 - Medium': themeColors.lightYellow,
  '2 - High': themeColors.yellow,
  '1 - Very high': themeColors.lightAlert,
  default: themeColors.greyLight,
});

export const valueGetterGoalCapital: GridValueGetter<Goal> = (params, row) =>
  Number(row?.targetCapital) ? getCurrencyString(row?.targetCapital) : UNDEFINED;

export const valueGetterGoalIncome: GridValueGetter<Goal> = (params, row) =>
  Number(row?.targetIncome) ? getCurrencyString(row?.targetIncome) : UNDEFINED;

export const valueGetterGoalDeadline: GridValueGetter<Goal> = (params, row) =>
  row?.targetEndDate ? dateFormatter(row?.targetEndDate) : UNDEFINED;

export const valueGetterGoalStatus: GridValueGetter<Goal> = (params, row) => {
  return row?.statusAtReview || UNDEFINED;
};

export const renderCellGoalStatus = RenderColor({
  [StatusAtReviewEnum.ON_TARGET]: themeColors.lightBlue,
  [StatusAtReviewEnum.AHEAD_OF_TARGET]: themeColors.lightGreen,
  [StatusAtReviewEnum.SATISFIED]: themeColors.green,
  [StatusAtReviewEnum.BEHIND_TARGET]: themeColors.lightAlert,
  [StatusAtReviewEnum.NO_LONGER_REQUIRED]: themeColors.greyLight,

  'On target': themeColors.lightBlue,
  'Ahead of target': themeColors.lightGreen,
  Satisfied: themeColors.green,
  'Behind target': themeColors.lightAlert,
  'No longer required': themeColors.greyLight,
  default: themeColors.greyLight,
});

export const valueGetterGoalCreated: GridValueGetter<Goal> = (params, row) =>
  row.dateCreated ? moment.utc(row.dateCreated).format('DD/MM/YYYY') : UNDEFINED;

const columns: GridColDef[] = [
  {
    field: 'status',
    headerName: 'Progress',
    flex: 1,
    editable: false,
    headerAlign: 'center',
    sortable: false,
    renderCell: renderCellGoalProgress,
    valueGetter: valueGetterGoalProgress,
  },
  {
    field: 'name',
    headerName: 'Name',
    flex: 4,
    editable: false,
    headerAlign: 'center',
    sortable: false,
    valueGetter: valueGetterGoalName,
    resizable: true,
  },
  {
    field: 'priority',
    headerName: 'Priority',
    flex: 1,
    editable: false,
    headerAlign: 'center',
    sortable: false,
    valueGetter: valueGetterGoalPriority,
    renderCell: renderCellGoalPriority,
  },
  {
    field: 'targetCapital',
    headerName: 'Target Capital',
    type: 'number',
    flex: 1,
    editable: false,
    headerAlign: 'center',
    sortable: false,
    align: 'right',
    valueGetter: valueGetterGoalCapital,
  },
  {
    field: 'targetIncome',
    headerName: 'Target Income',
    type: 'number',
    flex: 1,
    editable: false,
    headerAlign: 'center',
    sortable: false,
    align: 'right',
    valueGetter: valueGetterGoalIncome,
  },
  {
    field: 'targetDate',
    headerName: 'Deadline',
    type: 'string',
    flex: 1,
    editable: false,
    headerAlign: 'center',
    sortable: false,
    align: 'center',
    valueGetter: valueGetterGoalDeadline,
  },
  {
    field: 'achievementStatus',
    headerName: 'Status',
    flex: 1,
    editable: false,
    headerAlign: 'center',
    sortable: false,
    valueGetter: valueGetterGoalStatus,
    renderCell: renderCellGoalStatus,
  },
  {
    field: 'dateCreated',
    headerName: 'Created',
    flex: 1,
    editable: false,
    headerAlign: 'center',
    sortable: false,
    valueGetter: valueGetterGoalCreated,
  },
];

export default columns;
